import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { capitalize } from "lodash";
import CuoButton from "@/app/admin/_common/CuoButton";
import CuoConfirmDialog from "@/app/admin/_common/CuoConfirmDialog";
import PopOverLinkCopyButton from "@/app/admin/_common/PopOverLinkCopyButton";
import { Sheet, SheetContent, SheetTrigger } from "@/app/admin/_common/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import {
  ArrowLeft,
  Link2,
  MousePointerClick,
  Users,
  Trash2,
  MoreVertical,
  MoreHorizontal,
} from "lucide-react";
import { shortLinkUrl, type Link } from "../../linksPageModel";

export default function LinkDetailHeader({
  link,
  onDelete,
}: {
  link: Link | null;
  onDelete: () => Promise<void> | void;
}) {
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const visits = link?.visits ?? 0;
  const signupCount = (link?.signups?.new ?? 0) + (link?.signups?.repeat ?? 0);
  const url = link ? shortLinkUrl(link) : undefined;

  const titleContent = (
    // my-0 is load-bearing: Tailwind preflight is disabled in this app, so an
    // <h1> keeps the UA default margin (0.67em top and bottom).
    <h1 className="my-0 flex min-w-0 items-center gap-2 truncate py-0.5 text-[21px] font-bold leading-none tracking-tight text-foreground">
      <Link2 className="size-5 flex-none text-foreground" />
      {link?.name ?? "Link"}
    </h1>
  );

  const metaContent = (
    <div className="flex flex-wrap items-center gap-3 text-[12.5px] leading-none text-muted-foreground">
      <span className="inline-flex items-center gap-1">
        <MousePointerClick className="size-3.5" />
        {visits} {visits === 1 ? "visit" : "visits"}
      </span>
      <span className="inline-flex items-center gap-1">
        <Users className="size-3.5" />
        {signupCount} {signupCount === 1 ? "signup" : "signups"}
      </span>
      {link?.source && <span>{capitalize(link.source)}</span>}
      {link?.medium && <span>{capitalize(link.medium)}</span>}
    </div>
  );

  const mobileActionsSheet = (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="Link actions"
          className="inline-flex size-[30px] flex-none items-center justify-center rounded-lg border border-input bg-card text-foreground transition-colors hover:bg-accent hover:text-accent-foreground md:hidden"
        >
          <MoreVertical className="size-4 shrink-0" size={16} />
        </button>
      </SheetTrigger>
      <SheetContent side="bottom" className="rounded-t-xl bg-white p-0 md:hidden">
        <div className="flex flex-col pb-6 pt-6">
          <PopOverLinkCopyButton fullWidthRow title="Copy link" url={url} />
          <div className="mx-6 border-t border-border" />
          <CuoButton
            variant="ghost"
            label="Delete"
            icon={<Trash2 className="size-3.5" />}
            onClick={() => setConfirmDelete(true)}
            disabled={!link}
            className="h-11 w-full justify-start gap-2.5 rounded-none px-6 font-normal text-red-600 hover:text-red-700"
          />
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    // One flex column with a single gap drives the back-link / title / meta
    // rhythm, so the three lines stay evenly spaced instead of each carrying
    // its own margin.
    <div className="flex flex-col gap-2.5 border-b border-border bg-card/60 p-5 backdrop-blur-[2px]">
      <button
        type="button"
        onClick={() => navigate("/admin/marketing/links")}
        className="inline-flex w-fit items-center gap-1 text-[12.5px] leading-none text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" />
        Back to Links
      </button>

      {/* Mobile: title + actions trigger on one row, meta on its own row below */}
      <div className="flex flex-col gap-2.5 md:hidden">
        <div className="flex items-center justify-between gap-3">
          {titleContent}
          {mobileActionsSheet}
        </div>
        {metaContent}
      </div>

      {/* Desktop: title + meta grouped on the left, actions on the right */}
      <div className="hidden items-start justify-between gap-3 md:flex">
        <div className="flex min-w-0 flex-col gap-2.5">
          {titleContent}
          {metaContent}
        </div>
        <div className="flex flex-none items-center gap-2 pt-1">
          <PopOverLinkCopyButton title="Link" url={url} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="More actions"
                className="flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
              >
                <MoreHorizontal className="size-4 shrink-0" size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="text-red-600 focus:bg-red-50 focus:text-red-700"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="mr-2 size-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <CuoConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete link"
        message={`Are you sure you want to delete "${link?.name ?? ""}"? This action cannot be undone.`}
        onConfirm={onDelete}
        icon={<Trash2 />}
      />
    </div>
  );
}
